import { useMemo, useState } from 'react'
import { config } from '@/lib/config.runtime'
import type { AnalyticsState } from '@/hooks/useAnalytics'
import { useAnalytics } from '@/hooks/useAnalytics'
import {
  buildConversationsByPersonFigure,
  buildDailyLineFigure,
  buildSourceParetoFigure,
  buildSourceTreemapFigure,
  fillDailyGaps,
} from '@/lib/analytics/figures'
import { readChartPalette } from '@/lib/analytics/palette'
import ChartCard from '@/components/ChartCard'
import Plot from '@/components/Plot'
import styles from './UsageAnalytics.module.css'

export type UsageAnalyticsProps = {
  acquireToken: () => Promise<string>
  /** Inclusive ISO date bounds (YYYY-MM-DD). Owned by the page so the usage
   * and feedback surfaces share one range control. */
  from: string
  to: string
  /** Gate the load on the caller being a confirmed admin. */
  enabled?: boolean
}

/** Usage-analytics surface: fetches via useAnalytics for the range supplied by
 * the page and renders the chart grid. The date range is controlled by the
 * parent (the Statistikk page) so it stays in sync with the feedback chart. */
export default function UsageAnalytics({
  acquireToken,
  from,
  to,
  enabled = true,
}: UsageAnalyticsProps) {
  const palette = useMemo(() => readChartPalette(), [])
  const [showUnused, setShowUnused] = useState(false)

  const { state } = useAnalytics({
    baseUrl: config.apiBaseUrl,
    acquireToken,
    from,
    to,
    enabled,
  })

  return (
    <section className={styles.section} aria-label="Bruk og oppetid">
      <header className={styles.header}>
        <h2 className={styles.heading}>Bruk og oppetid</h2>
      </header>
      <AnalyticsBody
        state={state}
        palette={palette}
        from={from}
        to={to}
        showUnused={showUnused}
        onToggleUnused={setShowUnused}
      />
    </section>
  )
}

function AnalyticsBody({
  state,
  palette,
  from,
  to,
  showUnused,
  onToggleUnused,
}: {
  state: AnalyticsState
  palette: ReturnType<typeof readChartPalette>
  from: string
  to: string
  showUnused: boolean
  onToggleUnused: (next: boolean) => void
}) {
  const data = state.kind === 'ready' ? state.data : null
  const figures = useMemo(() => {
    if (data === null) return null
    // Zero-fill silent days so the daily charts show a continuous timeline
    // instead of skipping straight over periods with no usage.
    const daily = fillDailyGaps(data.daily, from, to)
    return {
      byPerson: buildConversationsByPersonFigure(daily, palette),
      conversations: buildDailyLineFigure(daily, 'conversations', 'Samtaler', palette),
      turns: buildDailyLineFigure(daily, 'turns', 'Meldinger', palette),
      users: buildDailyLineFigure(daily, 'uniqueUsers', 'Unike brukere', palette),
      median: buildDailyLineFigure(daily, 'medianTurnsPerUser', 'Median', palette),
      pareto: buildSourceParetoFigure(data.sources, palette),
      treemap: buildSourceTreemapFigure(data.sources, palette, showUnused),
    }
  }, [data, palette, from, to, showUnused])

  if (state.kind === 'loading') {
    return (
      <p className={styles.status} role="status">
        Laster bruksdata…
      </p>
    )
  }
  if (state.kind === 'error') {
    return (
      <p className={styles.status} role="alert">
        Kunne ikke laste bruksdata: {state.message}
      </p>
    )
  }
  if (data === null || figures === null) return null
  if (data.daily.length === 0 && data.sources.length === 0) {
    return <p className={styles.status}>Ingen data i valgt periode.</p>
  }

  return (
    <>
      <dl className={styles.summary}>
        <Stat label="Kilder totalt" value={data.totalSources} />
        <Stat label="Brukte kilder" value={data.usedSources} />
        <Stat label="Ubrukte kilder" value={data.unusedSources} />
      </dl>
      <div className={styles.grid}>
        <ChartCard
          wide
          title="Samtaler per person"
          description="Daglige samtaler, stablet per anonymisert bruker."
        >
          <Plot
            data={figures.byPerson.data}
            layout={figures.byPerson.layout}
            ariaLabel="Stablet søylediagram over samtaler per person per dag"
          />
        </ChartCard>
        <ChartCard title="Samtaler per dag">
          <Plot
            data={figures.conversations.data}
            layout={figures.conversations.layout}
            ariaLabel="Linjediagram over samtaler per dag"
          />
        </ChartCard>
        <ChartCard title="Meldinger per dag">
          <Plot
            data={figures.turns.data}
            layout={figures.turns.layout}
            ariaLabel="Linjediagram over meldinger per dag"
          />
        </ChartCard>
        <ChartCard title="Unike brukere per dag">
          <Plot
            data={figures.users.data}
            layout={figures.users.layout}
            ariaLabel="Linjediagram over unike brukere per dag"
          />
        </ChartCard>
        <ChartCard title="Median meldinger per bruker">
          <Plot
            data={figures.median.data}
            layout={figures.median.layout}
            ariaLabel="Linjediagram over median antall meldinger per bruker per dag"
          />
        </ChartCard>
        <ChartCard
          wide
          title="Kildebruk – fordeling"
          description="Hver kilde rangert etter antall siteringer; den lange halen er kilder som sjelden eller aldri brukes."
        >
          <Plot
            data={figures.pareto.data}
            layout={figures.pareto.layout}
            ariaLabel="Fordelingskurve over kildebruk rangert etter siteringer"
          />
        </ChartCard>
        <ChartCard
          wide
          tall
          title="Kildebruk – treemap"
          description="Gruppert per kildetype. Brukte kilder skaleres etter siteringer. Ubrukte kilder er skjult som standard — de er mange og fyller hele kartet."
          controls={
            <>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={showUnused}
                  onChange={(event) => onToggleUnused(event.currentTarget.checked)}
                />
                Vis ubrukte kilder
              </label>
              <ul className={styles.legend} aria-label="Kildetyper">
                {figures.treemap.legend.map((entry) => (
                  <li key={entry.label} className={styles.legendItem}>
                    <span
                      className={styles.swatch}
                      style={{ backgroundColor: entry.color }}
                      aria-hidden="true"
                    />
                    {entry.label}
                  </li>
                ))}
              </ul>
            </>
          }
        >
          <Plot
            data={figures.treemap.data}
            layout={figures.treemap.layout}
            ariaLabel="Treemap over kildebruk gruppert per kildetype"
          />
        </ChartCard>
      </div>
    </>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className={styles.stat}>
      <dt className={styles.statLabel}>{label}</dt>
      <dd className={styles.statValue}>{value.toLocaleString('nb-NO')}</dd>
    </div>
  )
}
